import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Dialog from "../../components/Dialog";
import useApiCall from "../../hooks/useApiCall";
import useToast from "../../hooks/useToast";
import { SLATE } from "../../constants/Colors";

const TARGET_ENVIRONMENTS = [
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
];

const PATH_SX = {
  fontFamily: "monospace",
  fontSize: "0.8125rem",
  color: SLATE[900],
  wordBreak: "break-all",
};

const CAPTION_SX = { fontSize: "0.75rem", color: SLATE[500] };

function formatTimestamp(value) {
  if (!value) return "never pushed";
  return new Date(value).toLocaleString();
}

/**
 * Promotes the intent models file that dev has already published to S3 up to
 * staging or production. It copies the file rather than regenerating it, so the
 * paths shown here are exactly what gets shipped.
 */
export default function PushToEnvironmentDialog({ open, onClose, tokens }) {
  const { makeRequest } = useApiCall();
  const toast = useToast();

  const [targetEnv, setTargetEnv] = useState("staging");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setPreview(null);
    setLoadingPreview(true);
    makeRequest(`/v1/intent_models/push_preview?target_environment=${targetEnv}`, {
      method: "GET",
      authTokens: tokens,
    })
      .then((response) => {
        if (!cancelled) setPreview(response);
      })
      .catch(() => {
        // error already toasted by useApiCall
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, targetEnv]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePush = async () => {
    setPushing(true);
    try {
      const response = await makeRequest(`/v1/intent_models/push_to_environment`, {
        method: "POST",
        body: { target_environment: targetEnv },
        authTokens: tokens,
      });
      toast.success(response?.message || `Intent models pushed to ${targetEnv}`);
      onClose();
    } catch {
      // error already toasted by useApiCall
    } finally {
      setPushing(false);
    }
  };

  const content = (
    <Box sx={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: SLATE[700], mb: "6px" }}>
          Target environment
        </Typography>
        <RadioGroup
          row
          value={targetEnv}
          onChange={(event) => setTargetEnv(event.target.value)}
        >
          {TARGET_ENVIRONMENTS.map((env) => (
            <FormControlLabel
              key={env.value}
              value={env.value}
              control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: "0.875rem" }}>{env.label}</Typography>}
            />
          ))}
        </RadioGroup>
      </Box>

      {loadingPreview && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography sx={CAPTION_SX}>Loading file details...</Typography>
        </Stack>
      )}

      {preview && (
        <>
          <Box>
            <Typography sx={CAPTION_SX}>Copy from</Typography>
            <Typography sx={PATH_SX}>{preview.source_path}</Typography>
            <Typography sx={CAPTION_SX}>
              {`${preview.source_intent_count} intents · updated ${formatTimestamp(preview.source_last_modified)}`}
            </Typography>
          </Box>
          <Box>
            <Typography sx={CAPTION_SX}>Copy to</Typography>
            <Typography sx={PATH_SX}>{preview.target_path}</Typography>
            <Typography sx={CAPTION_SX}>{`Currently ${formatTimestamp(preview.target_last_modified)}`}</Typography>
          </Box>
          <Typography sx={CAPTION_SX}>{`Bucket: ${preview.bucket}`}</Typography>
        </>
      )}
    </Box>
  );

  const actions = (
    <>
      <Button onClick={onClose} sx={{ textTransform: "none", color: SLATE[600] }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color={targetEnv === "production" ? "warning" : "primary"}
        onClick={handlePush}
        disabled={pushing || loadingPreview || !preview}
        sx={{ textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
      >
        {`Push to ${targetEnv}`}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Push intent models"
      content={content}
      actions={actions}
    />
  );
}
